import React, { useState, useEffect } from 'react'
import { format, addMonths } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { getBuildingOptions } from '../Steps/data/adminManager.data'
import API from '../../../../../../api/api'
import {
  getClients,
  selectClients,
  selectStatusIsLoadedAll,
  setLoadingState,
} from '../../../../../../reducers/clientsReducer'
import {
  selectBuildings,
  fetchSingleBuilding,
} from '../../../../../../reducers/buildingSelectorReducer'
import { selectUserData } from '../../../../../../reducers/userDataReducer'
import { createGiftCard } from '../../../../../../reducers/rewardsReducer'
import { mapClientToUser, setMinMaxValue } from '../helpers'
import {
  REWARDS_AVAILABLE_FLAG,
  getCheckboxHandler,
  getNamedHandler,
  getGiftCardCategoryOptions,
} from '../../../helpers'
import BaseView from '../BaseView/BaseView'
import ExternalCardItems from '../ExternalCardItems/ExternalCardItems'
import NonResidentView from '../NonResidentView/NonResidentView'
import RewardsExternalSelectRecipient from '../Steps/RewardsExternalSelectRecipient'
import RewardsSelectExternalGiftCard from '../Steps/RewardsSelectExternalGiftCard'
import RewardsExternalCardDetails from '../Steps/RewardsExternalCardDetails'
import RewardsExternalCardSummary from '../Steps/RewardsExternalCardSummary'
import SelectField from '../../../../../FormLegacy/FormFieldsV2/SelectField'
import CheckboxField from '../../../../../FormLegacy/FormFieldsV2/CheckboxField'
import SearchField from '../../../../../FormLegacy/FormFieldsV2/SearchField'
import TextField from '../../../../../FormLegacy/FormFieldsV2/TextField'
import SelectDropdownInput from '../../../../../SelectDropdownInput/SelectDropdownInput'
import { TextInput } from '@/components/ui/text-input'
import { useSnackbar } from '../../../../../../helpers/hooks/useSnackbar'

const External = ({
  goBack = () => {},
  closePopup = () => {},
  userData,
  getClients,
  users,
  allClientsLoaded,
  selectedBuilding,
  buildings,
  createGiftCard,
  setLoadingState,
  fetchSingleBuilding,
  // Note: pre-selects and locks the recipient - used when sending a gift from a resident's own
  // profile, where the resident is implied by context and must not be changed. Locking the
  // resident also implies locking the building (no separate `lockBuilding` prop needed) - the
  // caller already scopes `selectedBuilding` to the resident's building in that case. It also
  // suppresses the post-send redirect to the standalone Gift Cards page below (see onSubmit) -
  // that redirect would otherwise navigate a Resident Profile user away from the profile - in
  // favor of calling `onSuccess`, which the caller uses to refetch its own already-visible table.
  lockResident = undefined,
  onSuccess = () => {},
}) => {
  const { t } = useTranslation(['rewards-gift', 'rewards-table', 'rewards-loyalty'])
  const navigate = useNavigate()
  const [clients, setClients] = useState(users)
  const [cards, setCards] = useState([])
  const [formState, setFormState] = useState({
    building: selectedBuilding ? selectedBuilding.id : null,
    resident: lockResident ?? null,
    category: null,
    nonResident: false,
    recipientName: null,
    recipientEmail: null,
    card: null,
    description: null,
    cardValue: null,
    sendSms: false,
    nonResidentSmsNumber: '',
  })
  const [cardSearchValue, setCardSearchValue] = useState()
  const [isValidEmail, setIsValidEmail] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [clearInput, setClearInput] = useState(() => () => {})
  const { displaySnackbar } = useSnackbar()

  const isPropertyManager = userData.user.role === 'PropertyManager'

  const buildingData = isPropertyManager
    ? (userData.buildingIds || []).filter((building) => building[REWARDS_AVAILABLE_FLAG])
    : buildings || []

  const buildingOptions = getBuildingOptions(buildingData) || []

  const handleFieldChange = ({ target: { name, value } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const handleCheckboxChange = getCheckboxHandler(handleFieldChange)
  const handleSearchSelect = getNamedHandler(handleFieldChange)
  const handleCardSelect = getNamedHandler(handleFieldChange)
  const handleBuildingChange = (e) => {
    handleSearchSelect(null, 'resident')
    setClients([])
    clearInput()
    handleCardSelect(null, 'card')
    setCardSearchValue()
    handleFieldChange(e)
  }

  const fetchClients = async (query = '', isScrolling = false, buildingId = formState.building) => {
    const limit = 15
    const offset =
      clients.length % limit === 0
        ? clients.length / limit
        : (clients.length - (clients.length % limit)) / limit

    await getClients(isScrolling ? offset + 1 : 1, buildingId, undefined, query, limit)
  }

  useEffect(() => {
    if (!formState.building) {
      setCards([])
      return
    }
    ;(async () => {
      try {
        const { data } = await API.getAllExternalGiftCards(formState.building)
        setCards(data && data.length ? data : [])
      } catch (error) {
        console.error('Error fetching external gift cards:', error)
        setCards([])
      }
    })()
  }, [formState.building])

  useEffect(() => {
    setClients(users)
  }, [users])

  useEffect(() => {
    if (formState.card) {
      setFormState({
        ...formState,
        cardValue: null,
      })
    }
  }, [formState.card])

  const filteredCards = cards.filter(
    (item) => !cardSearchValue || item.name.toLowerCase().includes(cardSearchValue.toLowerCase()),
  )
  const onSubmitFinally = async () => {
    if (selectedBuilding.id) {
      await fetchSingleBuilding(selectedBuilding.id)
    }
    closePopup()
    setIsLoading(false)
  }

  const onSubmit = async () => {
    const requestData = {
      productCode: formState.card.code,
      amount: parseInt(formState.cardValue, 10),
      isNonResident: formState.nonResident,
      recipientName: formState.nonResident ? formState.recipientName : formState.resident.label,
      recipientEmail: formState.nonResident ? formState.recipientEmail : formState.resident.email,
      description: formState.description,
      buildingId: formState.building,
      category: formState.category,
      sendSms: formState.sendSms,
      ...(formState.nonResident &&
        formState.recipientPhone && {
          recipientPhone: '+1' + formState.recipientPhone,
        }),
    }

    if (!formState.nonResident) {
      requestData.clientId = formState.resident.value
    }

    setIsLoading(true)
    createGiftCard(requestData, 'external')
      .then(() => {
        displaySnackbar(
          t('rewards-gift:giftCardSentSuccess', {
            cardName: formState.card.name,
            recipientLabel: formState.nonResident
              ? formState.recipientName
              : formState.resident.label,
          }),
        )
        if (lockResident) {
          onSuccess()
        } else {
          navigate(`/dashboard/building-dashboard/rewards/gift-cards/${selectedBuilding.id}`)
        }
      })
      .catch(() =>
        displaySnackbar(
          t('rewards-gift:giftCardCreateError', { cardName: formState.card.name }),
          true,
        ),
      )
      .finally(onSubmitFinally)
  }

  const selectRecipientButtonEnabled =
    !!formState.building && formState.nonResident
      ? !!formState.recipientEmail &&
        !!formState.recipientName &&
        !!formState.category &&
        isValidEmail
      : !!formState.resident && !!formState.category

  const selectExternalGiftButtonEnabled = !!formState.card

  const isDescriptionLengthValid = (formState.description ?? '').length <= 128
  const externalCardDetailsButtonEnabled =
    !!formState.cardValue && !!formState.description && isDescriptionLengthValid

  const steps = [
    {
      Component: RewardsExternalSelectRecipient,
      title: t('rewards-gift:sendGiftSteps.selectRecipient'),
      config: {
        canGoNext: selectRecipientButtonEnabled,
        inputs: {
          // Note: locking the resident also locks the building (see `lockResident` note above)
          // and drops the "send to non-resident" option entirely - both would let the sender
          // pick a different recipient than the one this flow was opened for.
          ...(!lockResident && {
            first: {
              render: () => (
                <SelectField
                  name="building"
                  options={buildingOptions}
                  placeholder={t('rewards-gift:sendGiftForm.selectBuildingPlaceholder')}
                  onChange={handleBuildingChange}
                  value={formState.building}
                  selectStyle={{ width: '100%' }}
                />
              ),
              label: t('rewards-gift:sendGiftForm.selectBuildingLabel'),
            },
          }),
          second: {
            render: () =>
              lockResident ? (
                <TextInput disabled readOnly value={lockResident.label} />
              ) : (
                <SelectDropdownInput
                  placeholder={t('rewards-gift:sendGiftForm.clickAndTypeSearchResidentPlaceholder')}
                  data={users}
                  onSelectOption={(item) => handleSearchSelect(item, 'resident')}
                  fetchData={(skip, query, isScrolling, buildingId) =>
                    fetchClients(query, isScrolling, buildingId)
                  }
                  isLoadedAll={allClientsLoaded}
                  isDisabled={formState.nonResident}
                  setLoadingState={setLoadingState}
                  dataToRefetch={formState.building}
                  showSelectedInDropdown
                  clearOnDemand={setClearInput}
                />
              ),
            label: t('rewards-gift:sendGiftForm.selectResidentLabel'),
            disabled: formState.nonResident,
          },
          ...(!lockResident && {
            third: {
              render: () => (
                <CheckboxField
                  name="nonResident"
                  formState={formState}
                  onChange={(e) => {
                    handleCheckboxChange(e)
                    setFormState((prevState) => ({ ...prevState, recipientPhone: '' }))
                  }}
                />
              ),
              renderExtra: () =>
                formState.nonResident && (
                  <NonResidentView
                    handleFieldChange={handleFieldChange}
                    setIsValidEmail={setIsValidEmail}
                    formState={formState}
                    isValidEmail={isValidEmail}
                  />
                ),
              label: t('rewards-gift:sendGiftForm.sendCardToNonResidentLabel'),
            },
          }),
          fourth: {
            render: () => (
              <SelectField
                name="category"
                options={getGiftCardCategoryOptions(t)}
                placeholder={t('rewards-gift:sendGiftForm.selectCardCategoryPlaceholder')}
                onChange={handleFieldChange}
                value={formState.category}
                selectStyle={{ width: '100%' }}
              />
            ),
            label: t('rewards-table:giftCardCategoryPlaceholder'),
          },
        },
      },
    },
    {
      Component: RewardsSelectExternalGiftCard,
      title: t('rewards-gift:sendGiftSteps.selectGiftCard'),
      config: {
        canGoNext: selectExternalGiftButtonEnabled,
        inputs: {
          first: {
            render: () => (
              <SearchField
                placeholder={t('rewards-gift:sendGiftForm.searchByNameValuePlaceholder')}
                value={cardSearchValue}
                additionalClass="rewards-search-field"
                onChange={(e) => setCardSearchValue(e.target.value)}
              />
            ),
          },
          second: {
            render: () => (
              <ExternalCardItems
                onCardSelect={(card) => handleCardSelect(card, 'card')}
                selectedCardId={formState.card ? formState.card.id : null}
                cards={filteredCards}
              />
            ),
          },
        },
      },
    },
    {
      Component: RewardsExternalCardDetails,
      title: t('rewards-gift:sendGiftSteps.settings'),
      config: {
        canGoNext: externalCardDetailsButtonEnabled,
        inputs: {
          first: {
            render: () =>
              formState.card.availableDenominations ? (
                <SelectField
                  name="cardValue"
                  options={formState.card.availableDenominations.map((el) => ({
                    value: parseInt(el, 10),
                    label: `$${parseInt(el, 10)}`,
                  }))}
                  placeholder={t('rewards-gift:sendGiftForm.selectCardValuePlaceholder')}
                  onChange={handleFieldChange}
                  value={formState.cardValue}
                  selectStyle={{ width: '100%' }}
                />
              ) : (
                <React.Fragment>
                  <TextField
                    name="cardValue"
                    onChange={handleFieldChange}
                    onBlur={(e) =>
                      handleFieldChange(
                        setMinMaxValue(
                          e,
                          parseInt(formState.card.minimumValue, 10),
                          parseInt(formState.card.maximumValue, 10),
                        ),
                      )
                    }
                    value={formState.cardValue}
                    placeholder={t('rewards-gift:sendGiftForm.enterCardValuePlaceholder')}
                    style={{ width: '100%' }}
                  />
                  <span className="input-desc-label">{`$${parseInt(
                    formState.card.minimumValue,
                    10,
                  )} - $${parseInt(formState.card.maximumValue, 10)}`}</span>
                </React.Fragment>
              ),
            label: (
              <div>
                {t('rewards-gift:sendGiftForm.selectGiftCardValueLabel')}{' '}
                <span className="input-desc-label">
                  ({t('rewards-gift:sendGiftForm.requiredSuffix')})
                </span>
              </div>
            ),
          },
          ...(formState.card &&
            formState.card.expiryInMonths && {
              second: {
                render: () => (
                  <TextField
                    disabled
                    name="cardValue"
                    value={format(
                      addMonths(Date.now(), formState.card.expiryInMonths),
                      'MM/dd/yyyy',
                    )}
                    placeholder={t('rewards-gift:sendGiftForm.enterCardValuePlaceholder')}
                    style={{ width: '100%' }}
                  />
                ),
                label: t('rewards-table:rewardDetails.expirationPeriod'),
              },
            }),
          third: {
            render: () => (
              <React.Fragment>
                <TextField
                  name="description"
                  isError={!isDescriptionLengthValid}
                  errorLabel={t('rewards-gift:sendGiftForm.maxLength128Info')}
                  onChange={handleFieldChange}
                  value={formState.description}
                  placeholder={t('rewards-gift:sendGiftForm.enterDescriptionPlaceholder')}
                  style={{ width: '100%' }}
                />
                <span className="input-desc-label">
                  {t('rewards-gift:sendGiftForm.descriptionOrderInfo')}
                </span>
              </React.Fragment>
            ),
            label: (
              <div>
                {t('rewards-gift:sendGiftForm.descriptionLabel')}{' '}
                <span className="input-desc-label">
                  ({t('rewards-gift:sendGiftForm.requiredSuffix')})
                </span>
              </div>
            ),
          },
        },
      },
    },
    {
      Component: RewardsExternalCardSummary,
      title: t('rewards-gift:sendGiftSteps.summary'),
      config: {
        formState,
        onSubmit,
        currentBalance: selectedBuilding.rewardsBalance,
        isLoading,
        setFormState,
      },
    },
  ]

  return <BaseView steps={steps} goBack={goBack} />
}

External.propTypes = {
  goBack: PropTypes.func,
  userData: PropTypes.object,
  getClients: PropTypes.func,
  users: PropTypes.array,
  allClientsLoaded: PropTypes.bool,
  selectedBuilding: PropTypes.object,
  buildings: PropTypes.array,
  closePopup: PropTypes.func,
  createGiftCard: PropTypes.func,
  setLoadingState: PropTypes.func,
  fetchSingleBuilding: PropTypes.func,
  lockResident: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.number,
    email: PropTypes.string,
    phone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onSuccess: PropTypes.func,
}

export default connect(
  (state) => ({
    userData: selectUserData(state),
    buildings: selectBuildings(state),
    users: selectClients(state).map(mapClientToUser),
    allClientsLoaded: selectStatusIsLoadedAll(state),
  }),
  {
    getClients,
    createGiftCard,
    setLoadingState,
    fetchSingleBuilding,
  },
)(External)
